/**
 * TVSection Component
 * Right side TV section with channels, guest info, and special offers
 * Phase D2.2: Simulated IPTV with video playback
 */

import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { User, Maximize2, Volume2, VolumeX, Power } from 'lucide-react';
import { useKeyboardNavigation } from '@/hooks';
import { mockTVChannels } from '@/data/mockHotelData';
import { getChannelVideoUrl } from '@/data/tvChannelVideos';
import { PromotionsCarousel } from './PromotionsCarousel';
import type { Guest, Room, Stay } from '@/types';

interface TVSectionProps {
  enabled: boolean;
  onBillClick?: () => void;
  onChannelSelect?: (channelId: string) => void;
  tvFullscreen?: boolean;
  onFullscreenToggle?: () => void;
  // Phase D2.2: Current channel index for video switching
  currentChannelIndex?: number;
  // Phase D2.2: Controlled mute state
  isMuted?: boolean;
  onMuteToggle?: () => void;
  // Phase D2.2: TV ON/OFF state
  isTvOn?: boolean;
  onPowerToggle?: () => void;
  // Guest data from backend
  guest?: Guest | null;
  room?: Room | null;
  stay?: Stay | null;
}

// Items: guest info (0) + power (1) + mute (2) + fullscreen (3) + channels (4..N)
const TV_ITEM_COUNT = 4 + mockTVChannels.length;

export function TVSection({
  enabled,
  onBillClick,
  onChannelSelect,
  tvFullscreen = false,
  onFullscreenToggle,
  currentChannelIndex = 0,
  isMuted = true,
  onMuteToggle,
  isTvOn = true,
  onPowerToggle,
  guest,
  room,
  stay,
}: TVSectionProps) {
  // tvFullscreen is handled by TVFullscreenOverlay but prop kept for API consistency
  void tvFullscreen;

  // Video player ref for controlling playback
  const videoRef = useRef<HTMLVideoElement>(null);
  // Channel list item refs for scroll-into-view when D-Pad navigates beyond visible area
  const channelRefs = useRef<(HTMLDivElement | null)[]>([]);

  // React's `muted` prop doesn't reliably sync to the DOM property — set it directly
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // Get current channel data
  const currentChannel = mockTVChannels[currentChannelIndex] || mockTVChannels[0];
  const videoUrl = getChannelVideoUrl(currentChannel?.id || 'ch_hbo');

  const { isFocused, focusedIndex } = useKeyboardNavigation({
    gridColumns: 1,
    itemCount: TV_ITEM_COUNT,
    enabled,
    onSelect: (index) => {
      if (index === 0) {
        // Guest info -> show bill
        onBillClick?.();
      } else if (index === 1) {
        // Power button
        onPowerToggle?.();
      } else if (index === 2) {
        // Mute button
        if (isTvOn) onMuteToggle?.();
      } else if (index === 3) {
        // Fullscreen button
        if (isTvOn) onFullscreenToggle?.();
      } else {
        // Channel selected (index 4 = first channel)
        const channel = mockTVChannels[index - 4];
        if (channel) {
          onChannelSelect?.(channel.id);
        }
      }
    },
  });

  // Scroll focused channel into view when D-Pad moves past the visible area
  useEffect(() => {
    const channelIdx = focusedIndex - 4;
    if (channelIdx >= 0 && channelIdx < mockTVChannels.length) {
      channelRefs.current[channelIdx]?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex]);

  const guestName = guest ? `${guest.firstName} ${guest.lastName}` : 'Guest';
  const roomNumber = room?.roomNumber ?? '---';
  const stayEnd = stay ? new Date(stay.checkOutDate).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  }) : '---';

  return (
    <aside className="tv-section-root h-full flex flex-col bg-gradient-to-b from-zaxia-dark/70 to-zaxia-dark-deep/90 border-l border-white/[0.04] overflow-hidden">
      {/* Guest Info Header - flex-shrink-0 to maintain size */}
      <motion.div
        className={clsx(
          'tv-guest-header p-5 border-b border-white/[0.04] cursor-pointer keyboard-focusable',
          { 'keyboard-focused': enabled && isFocused(0) }
        )}
        animate={{ opacity: enabled && isFocused(0) ? 1 : 0.85 }}
        transition={{ duration: 0.2 }}
        onClick={onBillClick}
      >
        <div className="flex items-center gap-4">
          <div className="tv-guest-avatar w-11 h-11 rounded-full bg-zaxia-teal/20 flex items-center justify-center">
            <User className="w-5 h-5 text-zaxia-teal/80" />
          </div>
          <div>
            <div className="text-sm font-medium text-white/90">{guestName}</div>
            <div className="text-xs text-white/40 mt-0.5">
              Room {roomNumber} • Until {stayEnd}
            </div>
          </div>
        </div>
        {enabled && isFocused(0) && (
          <div className="mt-3 text-[10px] text-zaxia-teal/70 tracking-wide">
            Press Enter for Hotel Bill
          </div>
        )}
      </motion.div>

      {/* TV Mini Player - Phase D2.2: HTML5 Video */}
      <div className="relative aspect-video overflow-hidden tv-mini-player flex-shrink-0">
        {/* Video Player or TV OFF screen */}
        <AnimatePresence mode="wait">
          {isTvOn ? (
            <motion.video
              key={currentChannel?.id}
              ref={videoRef}
              src={videoUrl}
              autoPlay
              loop
              muted={isMuted}
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              style={{ willChange: 'opacity' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            />
          ) : (
            <motion.div
              key="tv-off"
              className="absolute inset-0 w-full h-full bg-black flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="text-center">
                <Power className="w-8 h-8 text-white/20 mx-auto mb-2" />
                <div className="text-xs text-white/30 uppercase tracking-wider">TV Off</div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Channel Info Overlay - only show when TV is ON */}
        {isTvOn && (
          <motion.div
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">{currentChannel?.logo}</span>
              <div>
                <div className="text-xs font-medium text-white/90">
                  {currentChannel?.name}
                </div>
                <div className="text-[10px] text-white/50">
                  {currentChannel?.currentProgram}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Subtle scan line effect */}
        <div className="scan-line opacity-30" />
      </div>

      {/* TV Controls */}
      <div className="flex items-center justify-center gap-5 py-4">
        <button
          className={clsx(
            "p-2.5 rounded-full transition-all duration-300 keyboard-focusable",
            isTvOn
              ? "bg-zaxia-teal/30 hover:bg-zaxia-teal/40"
              : "bg-white/[0.04] hover:bg-red-500/20",
            { 'keyboard-focused': enabled && isFocused(1) }
          )}
          onClick={onPowerToggle}
          title={isTvOn ? "Turn TV Off" : "Turn TV On"}
        >
          <Power className={clsx(
            "w-4 h-4",
            isTvOn ? "text-zaxia-teal" : "text-white/50"
          )} />
        </button>
        <button
          className={clsx(
            "p-2.5 rounded-full transition-all duration-300 keyboard-focusable",
            !isTvOn && "opacity-40 cursor-not-allowed",
            isMuted
              ? "bg-white/[0.04] hover:bg-zaxia-teal/20"
              : "bg-zaxia-teal/30 hover:bg-zaxia-teal/40",
            { 'keyboard-focused': enabled && isFocused(2) }
          )}
          onClick={isTvOn ? onMuteToggle : undefined}
          disabled={!isTvOn}
        >
          {isMuted ? (
            <VolumeX className="w-4 h-4 text-white/50" />
          ) : (
            <Volume2 className="w-4 h-4 text-white/80" />
          )}
        </button>
        <button
          className={clsx(
            "p-2.5 rounded-full bg-white/[0.04] hover:bg-zaxia-teal/20 transition-all duration-300 keyboard-focusable",
            !isTvOn && "opacity-40 cursor-not-allowed",
            { 'keyboard-focused': enabled && isFocused(3) }
          )}
          onClick={isTvOn ? onFullscreenToggle : undefined}
          disabled={!isTvOn}
        >
          <Maximize2 className="w-4 h-4 text-white/50" />
        </button>
      </div>

      {/* Channel List - fills remaining space, scrollable */}
      <div className="tv-channel-list flex-1 min-h-0 overflow-y-auto hide-scrollbar px-3 py-3">
        <div className="text-[10px] text-white/30 uppercase tracking-[0.1em] font-medium px-2 mb-3">
          Channels
        </div>
        {mockTVChannels.map((channel, index) => {
          const itemIndex = index + 4; // 0=guest, 1=power, 2=mute, 3=fullscreen, 4+=channels
          const focused = enabled && isFocused(itemIndex);
          const isActive = index === currentChannelIndex;

          return (
            <motion.div
              key={channel.id}
              ref={(el) => { channelRefs.current[index] = el as HTMLDivElement | null; }}
              className={clsx(
                'flex items-center gap-3 p-2.5 rounded-xl cursor-pointer mb-1 keyboard-focusable',
                'border transition-colors',
                {
                  'keyboard-focused bg-zaxia-teal/10 border-zaxia-teal/20': focused,
                  'bg-zaxia-teal/5 border-zaxia-teal/10': isActive && !focused,
                  'border-transparent': !focused && !isActive,
                }
              )}
              onClick={() => onChannelSelect?.(channel.id)}
            >
              <div className={clsx(
                "w-8 h-8 rounded-lg flex items-center justify-center text-base transition-colors",
                isActive ? "bg-zaxia-teal/20" : "bg-white/[0.04]"
              )}>
                {channel.logo}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white/85 truncate">
                  {channel.name}
                </div>
                <div className="text-[11px] text-white/35 truncate">
                  {channel.currentProgram}
                </div>
              </div>
              <div className="text-xs text-white/25 font-medium">
                {channel.number}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Phase D2.3: Cinematic Promotions Carousel - flex-shrink-0 to maintain size at bottom */}
      <div className="flex-shrink-0">
        <PromotionsCarousel />
      </div>

      {/* Keyboard Hint */}
      {enabled && (
        <div className="text-xs text-zaxia-gray/50 text-center pb-2">
          ↑↓ Navigate • Enter Select
        </div>
      )}
    </aside>
  );
}
